import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Copy, UserPlus, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { dateBR } from "../lib/format";
import { Pill } from "./common";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { NewRequestDialog } from "./NewRequestDialog";
import { admissionLink } from "./AdmissionCard";
import { STAGE, RemindActions } from "./AdmissionTracker";

function LinkPanel({ emp, adm, onChanged, channel }) {
  const url = admissionLink(adm.token);
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copiado"); } catch { toast.error("Não foi possível copiar."); } };
  return (
    <div className="space-y-3 min-w-0" data-testid="admission-link-panel">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        {adm.stage && <Pill cls={STAGE[adm.stage][1]} testid="admission-link-stage">{STAGE[adm.stage][0]}</Pill>}
        <span>Válido até {dateBR(adm.expires_at)}</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-50 border rounded-lg px-3 py-2.5 min-w-0">
        <Link2 className="h-4 w-4 text-sky-600 shrink-0" /><span data-testid="admission-link-url" className="font-mono text-[11px] truncate flex-1 min-w-0">{url}</span>
        <Button size="sm" variant="ghost" data-testid="admission-link-copy" onClick={copy}><Copy className="h-3.5 w-3.5 mr-1" />Copiar</Button>
      </div>
      {adm.stage === "enviado" ? <p className="text-sm text-emerald-700">Este candidato já enviou os dados.</p> : (
        <div>
          <div className="text-xs font-medium text-slate-600 mb-2">Enviar para {emp.name.split(" ")[0]}</div>
          <RemindActions item={{ employee_id: emp.id, name: emp.name, email: adm.email ?? emp.email, phone: emp.phone, token: adm.token }} emailLabel="Enviar por e-mail" only={channel || undefined} size={channel ? "default" : "sm"} onDone={onChanged} />
        </div>
      )}
    </div>
  );
}

const TITLES = { email: "Enviar link de admissão por e-mail", whatsapp: "Enviar link de admissão por WhatsApp" };

export function AdmissionLinkDialog({ open, onOpenChange, channel }) {
  const [emps, setEmps] = useState([]);
  const [sel, setSel] = useState("");
  const [adm, setAdm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hire, setHire] = useState(false);
  const loadEmps = () => api.get("/employees", { params: { status: "pre_admissao" } }).then((r) => setEmps(r.data));
  useEffect(() => { if (open) { loadEmps(); setSel(""); setAdm(null); } }, [open]);
  const pick = async (id) => {
    setSel(id); setAdm(null); setBusy(true);
    try {
      const { data } = await api.get(`/admissions/by-employee/${id}`).catch(async (e) => { if (e.response?.status === 404) return api.post(`/admissions/by-employee/${id}`); throw e; });
      setAdm(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const refresh = (r) => setAdm((a) => ({ ...a, ...(r ? { stage: r.stage, expires_at: r.expires_at, email: r.email } : {}) }));
  const emp = emps.find((e) => e.id === sel);
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="admission-link-dialog" className="max-w-lg w-[calc(100vw-2rem)]">
          <DialogHeader><DialogTitle data-testid="admission-link-title">{TITLES[channel] || "Link de admissão"}</DialogTitle><DialogDescription>O candidato preenche os dados e envia os documentos pelo celular. Você envia o link por e-mail ou WhatsApp.</DialogDescription></DialogHeader>
          <div className="space-y-4 min-w-0">
            <div className="flex flex-col sm:flex-row gap-2 min-w-0">
              <Select value={sel} onValueChange={pick}>
                <SelectTrigger data-testid="admission-link-candidate" className="flex-1 min-w-0 [&>span]:truncate"><SelectValue placeholder={emps.length ? "Escolha o candidato" : "Nenhum candidato em pré-admissão"} /></SelectTrigger>
                <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.position ? ` · ${e.position}` : ""}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" data-testid="admission-link-new-candidate" onClick={() => setHire(true)}><UserPlus className="h-4 w-4 mr-1.5" />Novo candidato</Button>
            </div>
            {busy && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Gerando link seguro...</div>}
            {emp && adm && <LinkPanel emp={emp} adm={adm} onChanged={refresh} channel={channel} />}
          </div>
        </DialogContent>
      </Dialog>
      <NewRequestDialog open={hire} type="admissao" onOpenChange={setHire} onCreated={async (r) => { await loadEmps(); if (r?.employee_id) pick(r.employee_id); }} />
    </>
  );
}
